import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, MoreHorizontal, Trash2, Edit, Star, Building2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { BankAccountForm } from './BankAccountForm'
import { toast } from '../../hooks/useToast'
import { getApiError } from '../../lib/api'
import {
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeleteBankAccount,
  useSetDefaultBankAccount,
} from '../../hooks/useSettings'
import { BankAccount, BankAccountCreate, BankAccountUpdate } from '../../types/settings'

export function BankAccountList() {
  const { t } = useTranslation()
  const { data: accounts = [], isLoading } = useBankAccounts()
  const createMutation = useCreateBankAccount()
  const updateMutation = useUpdateBankAccount()
  const deleteMutation = useDeleteBankAccount()
  const setDefaultMutation = useSetDefaultBankAccount()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null)

  const handleCreate = async (data: BankAccountCreate | BankAccountUpdate) => {
    try {
      await createMutation.mutateAsync(data as BankAccountCreate)
      toast({
        title: t('settings.bankAccounts.created'),
        description: t('settings.bankAccounts.createdDesc'),
      })
      setIsFormOpen(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleUpdate = async (data: BankAccountCreate | BankAccountUpdate) => {
    if (!editingAccount) return
    try {
      await updateMutation.mutateAsync({
        id: editingAccount.id,
        data: data as BankAccountUpdate,
      })
      toast({
        title: t('settings.bankAccounts.updated'),
        description: t('settings.bankAccounts.updatedDesc'),
      })
      setEditingAccount(null)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.bankAccounts.confirmDelete'))) return
    try {
      await deleteMutation.mutateAsync(id)
      toast({
        title: t('settings.bankAccounts.deleted'),
        description: t('settings.bankAccounts.deletedDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultMutation.mutateAsync(id)
      toast({
        title: t('settings.bankAccounts.defaultSet'),
        description: t('settings.bankAccounts.defaultSetDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">{t('settings.bankAccounts.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.bankAccounts.description')}
          </p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('settings.bankAccounts.add')}
        </Button>
      </div>

      {accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t('settings.bankAccounts.empty')}</p>
            <Button className="mt-4" onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('settings.bankAccounts.addFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {accounts.map((account) => (
            <Card key={account.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div className="space-y-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {account.name}
                    {account.isDefault && (
                      <Badge variant="secondary">
                        <Star className="mr-1 h-3 w-3" />
                        {t('settings.default')}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{account.bankName}</CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingAccount(account)}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                    {!account.isDefault && (
                      <DropdownMenuItem onClick={() => handleSetDefault(account.id)}>
                        <Star className="mr-2 h-4 w-4" />
                        {t('settings.bankAccounts.setAsDefault')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDelete(account.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {account.iban && (
                    <div>
                      <span className="text-muted-foreground">IBAN: </span>
                      <span className="font-mono">{account.iban}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">
                      {t('settings.bankAccounts.accountNumber')}:{' '}
                    </span>
                    <span className="font-mono">{account.accountNumber}</span>
                  </div>
                  {account.swiftBic && (
                    <div>
                      <span className="text-muted-foreground">SWIFT/BIC: </span>
                      <span className="font-mono">{account.swiftBic}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <BankAccountForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <BankAccountForm
        open={!!editingAccount}
        onClose={() => setEditingAccount(null)}
        onSubmit={handleUpdate}
        account={editingAccount}
        isLoading={updateMutation.isPending}
      />
    </div>
  )
}
